import React from 'react';
import Input from '@amzn/awsui-components-core/input';
import { COMMIT_ON_BLUR, useTypedText } from 'src/components/star-grid/hooks/useTypedText';

interface Props {
  readonly value: string;
  /** Called on blur, Enter or Escape — never per keystroke. */
  readonly onCommit: (next: string) => void;
  readonly ariaLabel?: string;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly onBlur?: () => void;
  readonly onEnter?: () => void;
  readonly onEscape?: () => void;
  readonly 'data-testid'?: string;
}

/**
 * A Studio text box whose keystrokes do not reach the shared draft (see {@link useTypedText}).
 * Escape commits too: the Studio has no separate cancel, so discarding would silently lose the edit.
 */
export const StudioTextInput: React.FC<Props> = ({
  value,
  onCommit,
  ariaLabel,
  placeholder,
  autoFocus,
  disabled,
  onBlur,
  onEnter,
  onEscape,
  'data-testid': testId
}) => {
  const typed = useTypedText(value, onCommit, COMMIT_ON_BLUR);
  return (
    <Input
      value={typed.value}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={({ detail }) => typed.onChange(detail.value)}
      onBlur={() => {
        typed.onBlur();
        onBlur?.();
      }}
      onKeyDown={({ detail }) => typed.onKey(detail.key, { onEnter, onEscape })}
      data-testid={testId}
    />
  );
};
